// WebGPU utilities for accelerated audio and image processing

// Ensure WebGPU constants are properly referenced
const { GPUBufferUsage, GPUMapMode } = navigator.gpu;

class WebGPUProcessor {
  constructor() {
    this.device = null;
    this.adapter = null;
    this.initialized = false;
  }

  async initialize() {
    if (this.initialized) return true;

    try {
      if (!navigator.gpu) {
        console.warn('WebGPU not supported, falling back to CPU processing');
        return false;
      }

      this.adapter = await navigator.gpu.requestAdapter({
        powerPreference: 'high-performance'
      });

      if (!this.adapter) {
        console.warn('WebGPU adapter not available');
        return false;
      }

      this.device = await this.adapter.requestDevice({
        requiredFeatures: [],
        requiredLimits: {}
      });

      this.initialized = true;
      console.log('WebGPU initialized successfully');
      return true;
    } catch (error) {
      console.warn('WebGPU initialization failed:', error);
      return false;
    }
  }

  // Accelerated FFT for beat detection
  async computeFFT(audioData) {
    if (!this.initialized) return null;

    const bufferSize = audioData.length;
    const inputBuffer = this.device.createBuffer({
      size: bufferSize * 4, // Float32 = 4 bytes
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    });

    const outputBuffer = this.device.createBuffer({
      size: bufferSize * 8, // Complex numbers (real + imaginary)
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC
    });

    const resultBuffer = this.device.createBuffer({
      size: bufferSize * 8,
      usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
    });

    // Upload audio data
    this.device.queue.writeBuffer(inputBuffer, 0, new Float32Array(audioData));

    const computeShader = this.device.createShaderModule({
      code: `
        @group(0) @binding(0) var<storage, read> input: array<f32>;
        @group(0) @binding(1) var<storage, read_write> output: array<vec2<f32>>;
        
        @compute @workgroup_size(64)
        fn main(@builtin(global_invocation_id) id: vec3<u32>) {
          let index = id.x;
          if (index >= arrayLength(&input)) { return; }
          
          let N = arrayLength(&input);
          var real = 0.0;
          var imag = 0.0;
          
          for (var n = 0u; n < N; n++) {
            let angle = -2.0 * 3.14159265359 * f32(index * n) / f32(N);
            real += input[n] * cos(angle);
            imag += input[n] * sin(angle);
          }
          
          output[index] = vec2<f32>(real, imag);
        }
      `
    });

    const computePipeline = this.device.createComputePipeline({
      layout: 'auto',
      compute: {
        module: computeShader,
        entryPoint: 'main'
      }
    });

    const bindGroup = this.device.createBindGroup({
      layout: computePipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: inputBuffer } },
        { binding: 1, resource: { buffer: outputBuffer } }
      ]
    });

    const commandEncoder = this.device.createCommandEncoder();
    const passEncoder = commandEncoder.beginComputePass();
    passEncoder.setPipeline(computePipeline);
    passEncoder.setBindGroup(0, bindGroup);
    passEncoder.dispatchWorkgroups(Math.ceil(bufferSize / 64));
    passEncoder.end();

    commandEncoder.copyBufferToBuffer(outputBuffer, 0, resultBuffer, 0, bufferSize * 8);
    
    const commands = commandEncoder.finish();
    this.device.queue.submit([commands]);

    await resultBuffer.mapAsync(GPUMapMode.READ);
    const result = new Float32Array(resultBuffer.getMappedRange());
    const complexResult = [];
    
    for (let i = 0; i < result.length; i += 2) {
      complexResult.push({
        real: result[i],
        imaginary: result[i + 1],
        magnitude: Math.sqrt(result[i] * result[i] + result[i + 1] * result[i + 1])
      });
    }

    resultBuffer.unmap();
    inputBuffer.destroy();
    outputBuffer.destroy();
    resultBuffer.destroy();

    return complexResult;
  }

  // GPU-accelerated beat detection
  async detectBeats(audioData, sampleRate) {
    if (!this.initialized) return null;

    const frameSize = 1024;
    const hopSize = 512;
    const beats = [];

    for (let i = 0; i + frameSize <= audioData.length; i += hopSize) {
      const frame = audioData.slice(i, i + frameSize);
      const fftResult = await this.computeFFT(frame);
      
      if (fftResult) {
        // Calculate spectral energy
        let energy = 0;
        let spectralCentroid = 0;
        let totalMagnitude = 0;

        for (let j = 0; j < fftResult.length / 2; j++) {
          const magnitude = fftResult[j].magnitude;
          energy += magnitude * magnitude;
          spectralCentroid += j * magnitude;
          totalMagnitude += magnitude;
        }

        energy = Math.sqrt(energy / (fftResult.length / 2));
        spectralCentroid = totalMagnitude > 0 ? spectralCentroid / totalMagnitude : 0;

        // Beat detection threshold
        if (energy > 0.05 && spectralCentroid > 100) {
          const timePosition = i / sampleRate;
          if (beats.length === 0 || timePosition - beats[beats.length - 1] > 0.1) {
            beats.push(timePosition);
          }
        }
      }
    }

    return beats;
  }

  // GPU-accelerated image processing for transitions
  async processImageTransition(imageData1, imageData2, progress, transitionType = 'crossfade', time = 0) {
    if (!this.initialized) return null;

    const { TRANSITION_SHADERS } = await import('./transitionShaders.js');
    
    const width = imageData1.width;
    const height = imageData1.height;
    const pixelCount = width * height;

    // Convert ImageData to Float32Array for GPU processing
    const img1FloatData = new Float32Array(pixelCount * 4);
    const img2FloatData = new Float32Array(pixelCount * 4);
    
    for (let i = 0; i < pixelCount * 4; i++) {
      img1FloatData[i] = imageData1.data[i] / 255.0;
      img2FloatData[i] = imageData2.data[i] / 255.0;
    }

    const inputBuffer1 = this.device.createBuffer({
      size: pixelCount * 4 * 4, // RGBA * 4 bytes
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    });

    const inputBuffer2 = this.device.createBuffer({
      size: pixelCount * 4 * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    });

    const outputBuffer = this.device.createBuffer({
      size: pixelCount * 4 * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC
    });

    const resultBuffer = this.device.createBuffer({
      size: pixelCount * 4 * 4,
      usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
    });

    // Uniform buffer for transition parameters
    const paramBuffer = this.device.createBuffer({
      size: 16, // 4 floats: progress, width, height, time
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
    });

    // Upload image data
    this.device.queue.writeBuffer(inputBuffer1, 0, img1FloatData);
    this.device.queue.writeBuffer(inputBuffer2, 0, img2FloatData);
    
    // Upload parameters
    const params = new Float32Array([progress, width, height, time]);
    this.device.queue.writeBuffer(paramBuffer, 0, params);

    // Get shader code
    const shaderCode = TRANSITION_SHADERS[transitionType] || TRANSITION_SHADERS.crossfade;
    const computeShader = this.device.createShaderModule({ code: shaderCode });

    const computePipeline = this.device.createComputePipeline({
      layout: 'auto',
      compute: {
        module: computeShader,
        entryPoint: 'main'
      }
    });

    const bindGroup = this.device.createBindGroup({
      layout: computePipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: inputBuffer1 } },
        { binding: 1, resource: { buffer: inputBuffer2 } },
        { binding: 2, resource: { buffer: outputBuffer } },
        { binding: 3, resource: { buffer: paramBuffer } }
      ]
    });

    const commandEncoder = this.device.createCommandEncoder();
    const passEncoder = commandEncoder.beginComputePass();
    passEncoder.setPipeline(computePipeline);
    passEncoder.setBindGroup(0, bindGroup);
    
    // Use 2D dispatch for better shader efficiency
    const workgroupsX = Math.ceil(width / 8);
    const workgroupsY = Math.ceil(height / 8);
    passEncoder.dispatchWorkgroups(workgroupsX, workgroupsY);
    passEncoder.end();

    commandEncoder.copyBufferToBuffer(outputBuffer, 0, resultBuffer, 0, pixelCount * 4 * 4);
    
    const commands = commandEncoder.finish();
    this.device.queue.submit([commands]);

    await resultBuffer.mapAsync(GPUMapMode.READ);
    const result = new Float32Array(resultBuffer.getMappedRange());
    
    // Convert back to Uint8ClampedArray
    const imageResult = new Uint8ClampedArray(pixelCount * 4);
    for (let i = 0; i < result.length; i++) {
      imageResult[i] = Math.round(Math.max(0, Math.min(1, result[i])) * 255);
    }
    
    const processedImageData = new ImageData(imageResult, width, height);

    resultBuffer.unmap();
    inputBuffer1.destroy();
    inputBuffer2.destroy();
    outputBuffer.destroy();
    resultBuffer.destroy();
    paramBuffer.destroy();

    return processedImageData;
  }

  // GPU-accelerated video frame generation
  async generateVideoFrames(images, beats, fps = 30, duration = 60) {
    if (!this.initialized) return null;

    const frames = [];
    const totalFrames = duration * fps;
    
    for (let frameIndex = 0; frameIndex < totalFrames; frameIndex++) {
      const currentTime = frameIndex / fps;
      
      // Find which beat segment we're in
      let beatIndex = 0;
      for (let i = 0; i < beats.length - 1; i++) {
        if (currentTime >= beats[i] && currentTime < beats[i + 1]) {
          beatIndex = i;
          break;
        }
      }
      
      const imageIndex = beatIndex % images.length;
      const nextImageIndex = (beatIndex + 1) % images.length;
      
      // Calculate transition progress within the beat segment
      const beatStart = beats[beatIndex] || 0;
      const beatEnd = beats[beatIndex + 1] || duration;
      const segmentProgress = (currentTime - beatStart) / (beatEnd - beatStart);
      
      // Apply transition effect
      let frameData;
      if (segmentProgress > 0.8) { // Transition in the last 20% of the segment
        const transitionProgress = (segmentProgress - 0.8) / 0.2;
        frameData = await this.processImageTransition(
          images[imageIndex],
          images[nextImageIndex],
          transitionProgress,
          'crossfade'
        );
      } else {
        frameData = images[imageIndex];
      }
      
      frames.push(frameData);
      
      // Progress callback
      if (frameIndex % 30 === 0) {
        const progress = (frameIndex / totalFrames) * 100;
        console.log(`Generated frame ${frameIndex}/${totalFrames} (${progress.toFixed(1)}%)`);
      }
    }
    
    return frames;
  }

  destroy() {
    if (this.device) {
      this.device.destroy();
    }
    this.initialized = false;
  }
}

export default WebGPUProcessor;